export function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

/**
 * Parse UTC offset from a timezone string (e.g. "(UTC+05:30) Chennai...", "(UTC-05:00)", "UTC+08:00", "(UTC)").
 * Returns { offsetHours, offsetMinutes } or null if parsing fails.
 */
export function parseUtcOffset(timezoneUtcName: string): { offsetHours: number; offsetMinutes: number } | null {
  if (!timezoneUtcName) {
    return null;
  }

  const offsetMatch = timezoneUtcName.match(/UTC([+-])(\d{1,2}):?(\d{2})?/i);
  if (offsetMatch) {
    const sign = offsetMatch[1];
    const hours = parseInt(offsetMatch[2], 10);
    const minutes = parseInt(offsetMatch[3] || '0', 10);

    const offsetHours = sign === '+' ? hours : -hours;
    const offsetMinutes = sign === '+' ? minutes : -minutes;

    return { offsetHours, offsetMinutes };
  }

  if (/UTC/i.test(timezoneUtcName)) {
    return { offsetHours: 0, offsetMinutes: 0 };
  }

  return null;
}

/**
 * Retrieve user profile timezone from localStorage if available.
 */
export function getUserTimezoneFromStorage(): string | null {
  try {
    if (typeof localStorage !== 'undefined') {
      const userProfileData = localStorage.getItem('userProfile');
      if (userProfileData) {
        const profile = JSON.parse(userProfileData);
        return profile?.timezoneUtcName || null;
      }
    }
  } catch {
    // Ignore parsing / storage errors
  }
  return null;
}

/**
 * Formats a timestamp into a string representation: "Month DaySuffix, Time Year (UTC Offset)"
 * Example: "May 15th, 10:48 AM 2026 (UTC +05:30)"
 * 
 * If timezoneUtcName is provided or found in user profile, formats based on that timezone.
 * Otherwise falls back to the browser's local timezone.
 * 
 * @param timestamp The timestamp to format (string, Date, or null/undefined)
 * @param timezoneUtcName Optional timezone string (e.g. "(UTC+05:30) India Standard Time")
 * @returns Formatted string or empty string if timestamp is invalid
 */
export function formatLastUpdated(
  timestamp: string | Date | null | undefined,
  timezoneUtcName?: string | null
): string {
  if (!timestamp) return '';

  let date: Date;
  if (timestamp instanceof Date) {
    date = timestamp;
  } else if (typeof timestamp === 'string') {
    const trimmed = timestamp.trim();
    // If ISO format without timezone suffix (e.g. '2026-05-15T10:30:00'), treat as UTC
    const isIsoWithoutTz = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(trimmed);
    date = new Date(isIsoWithoutTz ? `${trimmed}Z` : trimmed);
  } else {
    date = new Date(timestamp);
  }

  if (isNaN(date.getTime())) return '';

  const tzName = timezoneUtcName || getUserTimezoneFromStorage();
  const offset = tzName ? parseUtcOffset(tzName) : null;

  if (offset) {
    const totalOffsetMinutes = offset.offsetHours * 60 + offset.offsetMinutes;
    const targetDate = new Date(date.getTime() + totalOffsetMinutes * 60 * 1000);

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[targetDate.getUTCMonth()];
    const day = targetDate.getUTCDate();
    const suffix = getOrdinalSuffix(day);
    const utcHours = targetDate.getUTCHours();
    const utcMinutes = targetDate.getUTCMinutes();
    const hour12 = utcHours % 12 || 12;
    const ampm = utcHours >= 12 ? 'PM' : 'AM';
    const time = `${hour12}:${utcMinutes.toString().padStart(2, '0')} ${ampm}`;
    const year = targetDate.getUTCFullYear();

    const isNegative = offset.offsetHours < 0 || (offset.offsetHours === 0 && offset.offsetMinutes < 0);
    const offsetSign = isNegative ? '-' : '+';
    const absHours = Math.abs(offset.offsetHours).toString().padStart(2, '0');
    const absMinutes = Math.abs(offset.offsetMinutes).toString().padStart(2, '0');
    const timezoneStr = `UTC ${offsetSign}${absHours}:${absMinutes}`;

    return `${month} ${day}${suffix}, ${time} ${year} (${timezoneStr})`;
  }

  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();
  const suffix = getOrdinalSuffix(day);
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  const year = date.getFullYear();

  const localOffset = -date.getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(localOffset) / 60);
  const offsetMinutes = Math.abs(localOffset) % 60;
  const offsetSign = localOffset >= 0 ? '+' : '-';
  const timezoneStr = `UTC ${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;

  return `${month} ${day}${suffix}, ${time} ${year} (${timezoneStr})`;
}

/**
 * Returns timestamp formatted as yyyyMMddhhmmss (e.g. 20260924120904).
 */
export function getFormattedTimestamp(date: Date = new Date()): string {
  const yyyy = date.getFullYear();
  const MM = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${yyyy}${MM}${dd}${hh}${mm}${ss}`;
}

export class DateHelper {
  public static getFormattedTimestamp(date: Date = new Date()): string {
    return getFormattedTimestamp(date);
  }
}

